import {
  ChatbotContextualData,
  ChatbotExtractRequest,
  ChatbotExtractResponse,
  ChatMessage,
  DropdownOption,
  FollowUpQuestion,
  MeetingDetails,
  RecommendedAnswer
} from './chatbot.models';

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

export function disableMessageOptions(messages: ChatMessage[]): void {
  messages.forEach((m) => {
    if (m.role === 'bot') {
      m.optionsDisabled = true;
    }
  });
}

export function getOutboundTextForRecommendedAnswer(option: RecommendedAnswer): string {
  const value = option.value != null ? String(option.value).trim() : '';
  const text = option.text != null ? String(option.text).trim() : '';
  return value || text;
}

export function getOutboundTextForDropdownOption(option: DropdownOption): string {
  const id = option.id != null ? String(option.id).trim() : '';
  const label = option.label != null ? String(option.label).trim() : '';
  return id || label;
}

export function getOutboundTextForFollowUp(question: FollowUpQuestion): string {
  return question.text != null ? String(question.text).trim() : '';
}

/** Meeting date for extract API: YYYY-MM-DD. */
export function formatMeetingDateForBackend(date: string): string {
  return (date || '').trim();
}

/** Meeting time for extract API: HH:mm (e.g. 14:30). */
export function formatMeetingTimeForBackend(time: string): string {
  const t = (time || '').trim();
  if (!t) {
    return '';
  }
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) {
    return t.substring(0, 5);
  }
  return t;
}

export function buildMeetingTypeExtractRequest(meetingTypeId: string): ChatbotExtractRequest {
  return { meeting_type: (meetingTypeId || '').trim() };
}

export function buildStartDateExtractRequest(startDate: string): ChatbotExtractRequest {
  return { start_date: formatMeetingDateForBackend(startDate) };
}

export function buildStartTimeExtractRequest(startTime: string): ChatbotExtractRequest {
  return { start_time: formatMeetingTimeForBackend(startTime) };
}

export function buildStartDateTimeExtractRequest(
  date: string,
  time: string
): ChatbotExtractRequest {
  return {
    start_date: formatMeetingDateForBackend(date),
    start_time: formatMeetingTimeForBackend(time)
  };
}

/** Label shown in the chat bubble for structured extract requests. */
export function buildUserDisplayForExtractRequest(
  request: ChatbotExtractRequest,
  labelHint?: string
): string {
  if (labelHint) {
    return labelHint;
  }
  if (request.text) {
    return request.text;
  }
  const parts: string[] = [];
  if (request.meeting_type) {
    parts.push(`Meeting type: ${request.meeting_type}`);
  }
  if (request.start_date) {
    parts.push(`Date: ${request.start_date}`);
  }
  if (request.start_time) {
    parts.push(`Time: ${request.start_time}`);
  }
  return parts.join(', ') || 'Submitted';
}

export function hasExtractRequestPayload(request: ChatbotExtractRequest): boolean {
  return !!(
    (request.text && request.text.trim()) ||
    request.start_date ||
    request.start_time ||
    request.meeting_type
  );
}

export function dropdownToRecommendedAnswer(
  option: DropdownOption,
  category: string
): RecommendedAnswer {
  return {
    text: option.label,
    value: getOutboundTextForDropdownOption(option),
    category,
    icon: undefined
  };
}

/** Empty array from API → show calendar picker. */
export function needsDatePicker(ctx: ChatbotContextualData | undefined | null): boolean {
  return Array.isArray(ctx?.date_options) && ctx.date_options.length === 0;
}

/** Empty array from API → show clock picker. */
export function needsTimePicker(ctx: ChatbotContextualData | undefined | null): boolean {
  return Array.isArray(ctx?.time_options) && ctx.time_options.length === 0;
}

export function hasDateQuickOptions(ctx: ChatbotContextualData | undefined | null): boolean {
  return Array.isArray(ctx?.date_options) && ctx.date_options.length > 0;
}

export function hasTimeQuickOptions(ctx: ChatbotContextualData | undefined | null): boolean {
  return Array.isArray(ctx?.time_options) && ctx.time_options.length > 0;
}

export function hasMeetingTypeOptions(ctx: ChatbotContextualData | undefined | null): boolean {
  return Array.isArray(ctx?.meeting_type_options) && ctx.meeting_type_options.length > 0;
}

export function hasMeetingDetails(details: MeetingDetails | undefined): boolean {
  if (!details) {
    return false;
  }
  return !!(
    details.summary ||
    details.meeting_type ||
    details.start_date ||
    details.start_time ||
    details.end_time ||
    details.attendees ||
    details.agenda
  );
}

export function createBotMessageFromResponse(response: ChatbotExtractResponse | unknown): ChatMessage {
  const data = (response && typeof response === 'object' ? response : {}) as ChatbotExtractResponse;
  return {
    role: 'bot',
    content:
      data.response ||
      data.message ||
      'Sorry, I could not process your request.',
    recommendedAnswers: asArray(data.recommended_answers),
    followUpQuestions: asArray(data.follow_up_questions),
    meetingDetails: data.meeting_details,
    contextualData: data.contextual_data,
    actionType: data.action_type,
    optionsDisabled: false
  };
}

export function unwrapExtractResponse(response: unknown): ChatbotExtractResponse {
  if (!response || typeof response !== 'object') {
    return {};
  }
  const r = response as Record<string, unknown>;
  if (r.body && typeof r.body === 'object') {
    return r.body as ChatbotExtractResponse;
  }
  return response as ChatbotExtractResponse;
}
